import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { principalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';
import MobileFormDialog from '@components/mobile/MobileFormDialog';

const ManageExams = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exams, setExams] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formData, setFormData] = useState({
    name: '',
    academic_year: '',
    start_date: '',
    end_date: '',
    description: ''
  });

  const fetchExams = useCallback(async () => {
    try {
      setLoading(true);
      const response = await principalAPI.getExams();
      setExams(response.data.exams || []);
    } catch (error) {
      console.error('Error fetching exams:', error);
      showSnackbar('Error fetching exams', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  const handleOpenDialog = (exam = null) => {
    if (exam) {
      setEditingExam(exam);
      setFormData({
        name: exam.name,
        academic_year: exam.academic_year,
        start_date: exam.start_date || '',
        end_date: exam.end_date || '',
        description: exam.description || ''
      });
    } else {
      setEditingExam(null);
      setFormData({
        name: '',
        academic_year: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1),
        start_date: '',
        end_date: '',
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExam(null);
    setFormData({
      name: '',
      academic_year: '',
      start_date: '',
      end_date: '',
      description: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingExam) {
        await principalAPI.updateExam(editingExam.id, formData);
        showSnackbar('Exam updated successfully', 'success');
      } else {
        await principalAPI.createExam(formData);
        showSnackbar('Exam created successfully', 'success');
      }
      handleCloseDialog();
      fetchExams();
    } catch (error) {
      console.error('Error saving exam:', error);
      showSnackbar('Error saving exam', 'error');
    }
  };

  const handleDelete = async (examId) => {
    if (window.confirm('Are you sure you want to delete this exam? This will also delete all scheduled exam dates.')) {
      try {
        await principalAPI.deleteExam(examId);
        showSnackbar('Exam deleted successfully', 'success');
        fetchExams();
      } catch (error) {
        console.error('Error deleting exam:', error);
        showSnackbar('Error deleting exam', 'error');
      }
    }
  };

  const handleToggleStatus = async (exam) => {
    try {
      await principalAPI.updateExam(exam.id, { is_active: !exam.is_active });
      showSnackbar(`Exam ${exam.is_active ? 'deactivated' : 'activated'}`, 'success');
      fetchExams();
    } catch (error) {
      console.error('Error updating exam status:', error);
      showSnackbar('Error updating exam status', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Manage Exams
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Exam
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Box>

      {exams.length === 0 ? (
        <Paper>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No exams found. Click "Add Exam" to create one.
          </Typography>
        </Paper>
      ) : (
        <ResponsiveDataView
          rows={exams}
          keyExtractor={(exam) => exam.id}
          renderCard={(exam) => (
            <Card variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">{exam.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{exam.academic_year}</Typography>
                  </Box>
                  <Chip
                    label={exam.is_active ? 'Active' : 'Inactive'}
                    color={exam.is_active ? 'success' : 'default'}
                    size="small"
                    onClick={() => handleToggleStatus(exam)}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
                {exam.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{exam.description}</Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {exam.start_date ? new Date(exam.start_date).toLocaleDateString() : '-'} to {exam.end_date ? new Date(exam.end_date).toLocaleDateString() : '-'}
                </Typography>
                <Box display="flex" justifyContent="flex-end">
                  <IconButton size="small" color="primary" onClick={() => handleOpenDialog(exam)} title="Edit">
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(exam.id)} title="Delete">
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          )}
          table={
            <Paper>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Exam Name</strong></TableCell>
                      <TableCell><strong>Academic Year</strong></TableCell>
                      <TableCell><strong>Start Date</strong></TableCell>
                      <TableCell><strong>End Date</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exams.map((exam) => (
                      <TableRow key={exam.id} hover>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {exam.name}
                          </Typography>
                          {exam.description && (
                            <Typography variant="body2" color="text.secondary">
                              {exam.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{exam.academic_year}</TableCell>
                        <TableCell>
                          {exam.start_date ? new Date(exam.start_date).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          {exam.end_date ? new Date(exam.end_date).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={exam.is_active ? 'Active' : 'Inactive'}
                            color={exam.is_active ? 'success' : 'default'}
                            size="small"
                            onClick={() => handleToggleStatus(exam)}
                            sx={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(exam)}
                            title="Edit"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(exam.id)}
                            title="Delete"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          }
        />
      )}

      {/* Add/Edit Dialog */}
      <MobileFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={editingExam ? 'Edit Exam' : 'Add New Exam'}
        actions={
          <>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!formData.name || !formData.academic_year}
            >
              {editingExam ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Exam Name"
              placeholder="e.g., First Term, Second Term, Mid-term"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Academic Year"
              placeholder="e.g., 2025-2026"
              value={formData.academic_year}
              onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Grid>
        </Grid>
      </MobileFormDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManageExams;
